"use client"

/**
 * Section 04's collage as real layers (Eugene, 04.09; layer pass 2 the same day).
 *
 * Each tile is a stack: the photograph is its own layer, and everything on
 * top of it is DOM. The stack is read straight off Figma `314:9674`, node by
 * node, and every number below is a Figma pixel — `--px` (declared on the
 * collage) converts one Figma pixel into the collage's current width, so the
 * whole thing scales without any value here changing. 1512 Figma px = 100%.
 *
 * WHERE THE PIECES COME FROM
 * - Photographs are exported from the tile itself with every other layer
 *   hidden, so Figma's progressive layer blur (hero: bottom 40%, chat: bottom
 *   two thirds, phone: a mild ramp) is baked exactly as the design renders
 *   it, at the position the designer put it. Reproducing a progressive blur
 *   in CSS is a stack of masked copies — here it is one JPEG.
 * - The phone is the design's own iPhone 17 Pro component, exported as two
 *   PNGs: the body with an empty screen, and the Dynamic Island on its own,
 *   so the screen markup slots between them the way it does in the device.
 * - The screen is the report app's mobile home view (the AHD-12116 preview,
 *   390px wide), transcribed element for element with the app's own values:
 *   `--ap` is one app pixel, 390 of them across the 195.87px screen. It is
 *   the app minus its scripts — no nav, no delta animation, no interaction.
 *   The score card's gradient, grain and texture are the app's own.
 * - The MRI is a single scan, cropped as Figma crops it (CROP transform),
 *   nothing under it and nothing over it.
 * - The action row and the doctor's note reuse the membership page's
 *   `_membership` keys; the two chat bubbles are the membership page's
 *   expert bubbles; the phone screen reads the report page's `report.mockup*`
 *   strings. Everything on screen is therefore translated today.
 *
 * HOW THINGS ARRIVE
 * The box itself fades in with scroll (`.hv3-plan-tile`, in the section).
 * What is INSIDE a box does not scrub with the scrollbar: once the box is
 * about two thirds in, the section flips `data-in` on it and the layers play
 * like messages arriving on a phone — a short, heavy rise from the corner
 * the avatar sits in, one after another — while the phone rides up from
 * mid-height. Scrolling back up drops `data-in` and they leave quickly, so
 * the entrance replays on the way down. Both use one spring (stiffness 300,
 * damping 30, no bounce), rendered as a CSS `linear()` curve. The box's own
 * progress is eased in the section (a per-frame glide towards the scroll
 * position), so the layers ride on a moving box rather than a jumping one.
 */

import { blobMediaUrl } from "@/lib/blob-url"
import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import { ChevronRightIcon } from "@/components/ui/icons"
import { membershipKeys as mk } from "@/i18n/membership-keys"
import { useHomeV4Copy } from "./home-v4-copy"

const L = blobMediaUrl("/images/marketing/home-v3/layers")

/** One Figma pixel, as a length. See `--px` in the collage's style block. */
const fp = (n: number) => `calc(${n} * var(--px))`
/** Absolute box in Figma pixels, relative to the tile. */
const box = (x: number, y: number, w: number, h?: number) => ({
  left: fp(x),
  top: fp(y),
  width: fp(w),
  ...(h === undefined ? {} : { height: fp(h) }),
})

/** Delay, in seconds, before a layer starts its entrance once the tile is in. */
const at = (seconds: number) =>
  ({ "--d": `${seconds}s` }) as React.CSSProperties

/** Figma 314:9828 — where the screen and the island sit inside the device. */
const PHONE = { w: 203.51, h: 427 }
const SCREEN = { x: 4, y: 3, w: 195.87, h: 417.06, r: 33.5 } // measured from the exported frame: interior sits inside a ~1px black bezel ring
const ISLAND = { x: 72, y: 13.44, w: 59.73, h: 17.94 }

export function PlanTileStyles() {
  return (
    <style>{`
      /* 1512 Figma px = 100cqw of the collage, so --px is one Figma pixel at
         the collage's current size. Declared on the container; container-query
         units resolve at the use site, i.e. against this same element. */
      .hv3-collage{
        container-type:inline-size;--px:0.0661376cqw;
        /* spring(300,30) — heavy, no bounce. Was the phone's alone; the cards
           and bubbles had the membership page's spring(500,24) with its 13.6%
           overshoot, and the whole collage read as jumpy (Eugene, 07.09). */
        --hv3-ride:linear(0,0.018,0.063,0.127,0.201,0.28,0.36,0.439,0.513,0.582,0.644,0.701,0.751,0.794,0.832,0.864,0.892,0.915,0.934,0.95,0.963,0.973,0.981,0.988,0.993,0.997,0.999,1.001,1.003,1.004,1.004,1.004,1.004);
      }

      /* ---- entrances: triggered by data-in on the tile, not scrubbed ---- */
      .hv3-lay{opacity:0;transition:opacity .18s ease,transform .18s ease;will-change:transform,opacity}
      .hv3-lay-pop{transform:translateY(calc(10 * var(--px))) scale(.96);transform-origin:bottom left}
      .hv3-lay-pop.hv3-from-center{transform-origin:center}
      .hv3-lay-up{transform:translateY(46%);opacity:.5}
      /* Slower than the .4/.6 first tried: with the box itself already
         gliding, layers that snapped in on top of it read as flicker
         (Eugene, 10.09). */
      [data-in] .hv3-lay{opacity:1;transform:none;transition:opacity .7s ease-out var(--d,0s),transform .95s var(--hv3-ride) var(--d,0s)}
      [data-in] .hv3-lay-up{transition:opacity .8s ease-out var(--d,0s),transform 1s var(--hv3-ride) var(--d,0s)}

      /* ---- shared tile chrome ---- */
      .hv3-t{position:absolute;inset:0;overflow:hidden;border-radius:calc(24 * var(--px))}
      /* The tiles are placed in percentages of the collage, so at most window
         widths their edges land between device pixels and the rounded clip
         leaves a hairline of page background down the right and bottom edges
         (Eugene, 08.09). The three full-bleed photographs therefore overshoot
         their box by half a pixel all round: the clip still decides the shape,
         it just never runs out of picture. Only direct children — the phone
         body and the avatars are positioned against overlays and must not
         grow. The rule is !important because next/image writes its own
         inset inline, and max-width because preflight's img{max-width:100%}
         would otherwise clamp the extra pixel straight back off. */
      .hv3-t>img[data-nimg="fill"]{inset:-0.5px!important;width:calc(100% + 1px)!important;height:calc(100% + 1px)!important;max-width:none!important}
      /* The man at the laptop sits under the two cards on the left; scaled
         up a touch and pushed right so his face clears them (Eugene, 10.09).
         translate then scale: the scale adds 11% each side, the shift
         spends 10% of it on the left and 5% at the top so his head keeps
         its air. */
      .hv3-t>img.hv3-hero-img{transform:translate(10%,5%) scale(1.22);transform-origin:50% 50%}

      /* ---- chat (Figma 314:9140) ---- */
      .hv3-chat{position:absolute;display:flex;flex-direction:column}
      .hv3-chat-me{position:relative;flex-shrink:0}
      /* The messages arrive the way they do in a chat: the first sits on the
         avatar, then the second grows in beneath it and lifts it (Eugene,
         10.09). Each bubble rides in a 0fr grid row that costs no height
         until its turn, so the bottom-anchored column has nothing to stand
         on until a message lands. The 8px rhythm is the bubble's own margin
         rather than the column's gap, so it collapses with the row. */
      /* min-height:0 twice over: the slot is a flex item, whose automatic
         minimum size would otherwise hold the row open at its content. */
      .hv3-slot{display:grid;grid-template-rows:0fr;min-height:0;transition:grid-template-rows .5s var(--hv3-ride) var(--d,0s)}
      .hv3-slot>div{min-height:0;overflow:hidden}
      .hv3-slot .hv3-bub{margin-bottom:calc(8 * var(--px))}
      [data-in] .hv3-slot{grid-template-rows:1fr}
      /* A message lands faster than the collage's own layers: the .7/.95s
         of .hv3-lay left the first bubble still arriving when the second
         one started, and the push read as one movement. */
      [data-in] .hv3-slot .hv3-bub{transition:opacity .4s ease-out var(--d,0s),transform .5s var(--hv3-ride) var(--d,0s)}
      .hv3-bub{position:relative;display:flex;align-items:flex-start;border-radius:calc(18 * var(--px));padding:calc(16 * var(--px));background:rgba(17,17,17,.56);-webkit-backdrop-filter:blur(calc(24 * var(--px)));backdrop-filter:blur(calc(24 * var(--px)));-webkit-backdrop-filter:blur(calc(3.55 * var(--px)))} /* ds-color-ignore — Figma's translucent bubble, mirrors ChatBubble in membership-year.tsx */
      /* 14/18.18, the doctor's note's rung rather than Figma's 16/24: at 16 the
         two bubbles read louder than every other card in the collage
         (Eugene, 08.09). */
      .hv3-bub p{font-size:calc(14 * var(--px));line-height:calc(18.18 * var(--px));color:#fff;margin:0} /* ds-color-ignore — white on a photograph */
      /* 4px stroke, centred: 2px over the picture's edge, 2px outside it. */
      .hv3-avatar{position:absolute;border-radius:999px;overflow:hidden}

      /* ---- hero cards (Figma 314:9106 / 314:9613): 26% black, no stroke, no blur ---- */
      .hv3-card{position:absolute;border-radius:calc(12.08 * var(--px));background:rgba(0,0,0,.26);-webkit-backdrop-filter:blur(calc(24 * var(--px)));backdrop-filter:blur(calc(24 * var(--px)))} /* ds-color-ignore — Figma's dark glass card */
      .hv3-ct{font-size:calc(14 * var(--px));line-height:calc(18.18 * var(--px));letter-spacing:calc(0.09 * var(--px));font-weight:500;color:#fff;margin:0} /* ds-color-ignore */
      .hv3-cd{font-size:calc(14 * var(--px));line-height:calc(18.18 * var(--px));color:#fff;margin:0} /* ds-color-ignore */
      /* hero — the cholesterol line: white on the dark glass, one face throughout. */

      /* ---- phone screen: the report app's mobile home view, 390 app px wide.
         --ap is one app pixel on the 195.87px screen, so every length below
         is the app's own Tailwind value (p-4 = 16, the 44px number = 44, …). ---- */
      .hv3-scr{--ap:calc(var(--px) * 0.50223);position:absolute;overflow:hidden;background:var(--color-neutral-50);color:var(--color-neutral-900);display:flex;flex-direction:column;gap:calc(24 * var(--ap));padding:calc(80 * var(--ap)) calc(16 * var(--ap)) 0}
      .hv3-scr p{margin:0}
      .hv3-rp-intro{display:flex;flex-direction:column;gap:calc(8 * var(--ap))}
      .hv3-rp-hi{font-size:calc(16 * var(--ap));line-height:calc(24 * var(--ap));color:var(--color-neutral-500)}
      .hv3-rp-lede{font-size:calc(26 * var(--ap));line-height:calc(34 * var(--ap));font-weight:500}
      /* the report block: white, radius 20, p-6 max-sm:px-3 */
      .hv3-rp-block{display:flex;flex-direction:column;gap:calc(24 * var(--ap));border-radius:calc(20 * var(--ap));background:#fff;padding:calc(24 * var(--ap)) calc(12 * var(--ap))} /* ds-color-ignore — the app's card white */
      .hv3-rp-head{display:flex;align-items:center;justify-content:space-between;gap:calc(12 * var(--ap))}
      /* The app sets this label in caps with CSS; here it stays as written, the
         translated string is the copy and CSS casing breaks German. */
      .hv3-rp-title{display:flex;align-items:center;gap:calc(10 * var(--ap));font-size:calc(14 * var(--ap));line-height:calc(17.5 * var(--ap));font-weight:500;letter-spacing:calc(0.35 * var(--ap))}
      .hv3-rp-ico{display:flex;align-items:center;justify-content:center;flex-shrink:0;width:calc(32 * var(--ap));height:calc(32 * var(--ap));border-radius:calc(8 * var(--ap));background:var(--color-aubergine-100);color:var(--color-aubergine-800)}
      .hv3-rp-ico svg{width:calc(19 * var(--ap));height:calc(19 * var(--ap))}
      .hv3-rp-link{display:flex;align-items:center;flex-shrink:0;gap:calc(4 * var(--ap));padding:calc(8 * var(--ap));margin-right:calc(-8 * var(--ap));border-radius:calc(12 * var(--ap));font-size:calc(14 * var(--ap));line-height:calc(17.5 * var(--ap));font-weight:500;white-space:nowrap;color:var(--color-aubergine-800)}
      .hv3-rp-link svg{width:calc(16 * var(--ap));height:calc(16 * var(--ap))}
      .hv3-rp-cards{display:flex;flex-direction:column;gap:calc(12 * var(--ap))}
      .hv3-rp-card{position:relative;display:flex;flex-direction:column;overflow:hidden;height:calc(184 * var(--ap));padding:calc(16 * var(--ap));border-radius:calc(16 * var(--ap));background:var(--color-neutral-50)}
      /* score card: the app's radial gradient, its texture at hard-light 41%, its fractal grain at overlay 12% */
      .hv3-rp-green{color:#fff;background-image:url("data:image/svg+xml;utf8,<svg viewBox='0 0 247.33 194' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'><rect x='0' y='0' height='100%' width='100%' fill='url(%23grad)' opacity='1'/><defs><radialGradient id='grad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' gradientTransform='matrix(-15.8 -23.5 30.139 -20.263 296.5 248)'><stop stop-color='rgba(182,209,152,1)' offset='0'/><stop stop-color='rgba(184,201,121,1)' offset='0.29291'/><stop stop-color='rgba(148,176,102,1)' offset='0.43046'/><stop stop-color='rgba(112,151,82,1)' offset='0.56801'/><stop stop-color='rgba(85,136,66,1)' offset='0.67601'/><stop stop-color='rgba(58,121,49,1)' offset='0.784'/><stop stop-color='rgba(32,107,33,1)' offset='0.892'/><stop stop-color='rgba(18,99,24,1)' offset='0.946'/><stop stop-color='rgba(5,92,16,1)' offset='1'/></radialGradient></defs></svg>");background-size:100% 100%;background-repeat:no-repeat} /* ds-color-ignore — the report app's own score-card gradient */
      .hv3-rp-tex{position:absolute;top:-9.79%;left:-24.06%;width:147.58%;height:141.24%;max-width:none;object-fit:cover;opacity:.41;mix-blend-mode:hard-light;pointer-events:none}
      .hv3-rp-grain{position:absolute;inset:0;opacity:.12;mix-blend-mode:overlay;pointer-events:none;background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/></filter><rect width='140' height='140' filter='url(%23n)'/></svg>");background-size:calc(140 * var(--ap)) calc(140 * var(--ap));background-repeat:repeat}
      .hv3-rp-body{position:relative;display:flex;flex-direction:column;flex-grow:1;min-height:0}
      .hv3-rp-num{font-size:calc(44 * var(--ap));line-height:calc(44 * var(--ap));font-weight:500}
      .hv3-rp-lbl{height:calc(24 * var(--ap));font-size:calc(14 * var(--ap));line-height:calc(18 * var(--ap))}
      .hv3-rp-viz{position:relative;flex-shrink:0;padding-top:calc(20 * var(--ap))}
      .hv3-rp-foot{margin-top:auto;max-width:calc(216 * var(--ap));padding-top:calc(16 * var(--ap));font-size:calc(13 * var(--ap));line-height:calc(16 * var(--ap))}
      .hv3-rp-cap{display:block;line-height:150%}
      /* Keep the origin and delta badge on one shared anchor at every scale. */
      .hv3-rp-track{position:relative;height:calc(8 * var(--ap))}
      .hv3-rp-rail{position:absolute;inset:0;border-radius:calc(40 * var(--ap));background:rgba(255,255,255,.3)} /* ds-color-ignore */
      .hv3-rp-origin{position:absolute;top:50%;left:50%;width:calc(4 * var(--ap));height:calc(4 * var(--ap));transform:translate(-50%,-50%);border-radius:50%;background:rgba(255,255,255,.12);box-shadow:0 0 0 calc(1 * var(--ap)) #fff} /* ds-color-ignore */
      .hv3-rp-pill{position:absolute;top:50%;transform:translate(-50%,-50%);display:flex;align-items:center;height:calc(20 * var(--ap));padding:0 calc(5 * var(--ap)) 0 calc(4 * var(--ap));border-radius:calc(40 * var(--ap));background:#fff;color:var(--color-neutral-900);font-size:calc(11 * var(--ap));line-height:calc(14 * var(--ap));font-weight:700;white-space:nowrap} /* ds-color-ignore */
      .hv3-rp-pill svg{width:calc(12 * var(--ap));height:calc(16 * var(--ap))}
      /* findings / biomarkers: segmented bar, legend rows, outline chips */
      .hv3-rp-seg{display:flex;height:calc(8 * var(--ap));gap:calc(2 * var(--ap))}
      .hv3-rp-seg i{height:100%;border-radius:999px}
      .hv3-rp-g1{background:var(--color-deep-green-500)}
      .hv3-rp-g2{background:var(--color-light-green-400)}
      .hv3-rp-g3{background:var(--color-terracotta-300)}
      .hv3-rp-legend{display:flex;flex-direction:column;gap:calc(6 * var(--ap))}
      .hv3-rp-row{display:flex;align-items:baseline;gap:calc(6 * var(--ap));min-width:0}
      .hv3-rp-dot{flex-shrink:0;width:calc(6 * var(--ap));height:calc(6 * var(--ap));border-radius:50%;transform:translateY(calc(-2 * var(--ap)))}
      .hv3-rp-n{flex-shrink:0;font-weight:500}
      .hv3-rp-chips{display:flex;flex-wrap:wrap;gap:calc(8 * var(--ap))}
      .hv3-rp-chip{display:flex;align-items:center;justify-content:center;gap:calc(6 * var(--ap));padding:calc(2 * var(--ap)) calc(8 * var(--ap)) calc(1 * var(--ap));border-radius:calc(40 * var(--ap));border:calc(1 * var(--ap)) solid var(--color-neutral-100)}
    `}</style>
  )
}

/* ------------------------------------------------------------------ */

/**
 * Figma 314:9099 — the lake photograph (its own layer, mild progressive
 * blur), then the device riding up over it: body, screen, island, in that
 * order, exactly as the component is layered.
 */

/** The app's clipboard-with-bars mark (Tabler `clipboard-data`); not in icons.tsx. */
function ClipboardGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
      <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
      <path d="M9 17v-5" />
      <path d="M12 17v-1" />
      <path d="M15 17v-3" />
    </svg>
  )
}

/** Lucide `arrow-down` at the pill's 3px stroke. */
function ArrowDownGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  )
}

export function PlanPhoneTile({ style }: { style?: React.CSSProperties }) {
  const s = useHomeV4Copy().PLAN_TILES.phone
  // The screen is the report app's home view, so it reads the report page's
  // own `report.mockup*` strings rather than a second copy of them. Every
  // label here is therefore already translated into all five locales — which
  // is the whole reason the screen is markup and not a PNG.
  const r = useTranslations("report") as unknown as (k: string) => string
  const m = useTranslations("_membership") as unknown as (k: string) => string
  const findings = s.findingsMinorCount + s.findingsObservationCount
  const biomarkers =
    s.biomarkerOptimal + s.biomarkerInRange + s.biomarkerOutOfRange
  const pillAt = { left: "50%" }
  return (
    <div className="hv3-t" style={style}>
      <Image
        src={`${L}/bg-phone.jpg`}
        alt=""
        fill
        sizes="(min-width: 1024px) 420px, 40vw"
        className="object-cover"
      />
      <div
        className="hv3-lay hv3-lay-up absolute"
        style={{ ...box(87, 34, PHONE.w, PHONE.h), ...at(0.05) }}
      >
        <Image
          src={`${L}/phone-body-alpha.png`}
          alt=""
          fill
          sizes="(min-width: 1024px) 240px, 24vw"
          className="object-fill"
        />
        <div
          className="hv3-scr"
          style={{
            ...box(SCREEN.x, SCREEN.y, SCREEN.w, SCREEN.h),
            borderRadius: fp(SCREEN.r),
          }}
        >
          {/* .home-intro — "Hello," carries the comma in every locale, so the name follows it. */}
          <div className="hv3-rp-intro">
            <p className="hv3-rp-hi">{`${r("mockupHello")} ${s.memberName}`}</p>
            <p className="hv3-rp-lede">{m(mk.overlayProgressBody)}</p>
          </div>

          {/* .home-report-block */}
          <div className="hv3-rp-block">
            <div className="hv3-rp-head">
              <p className="hv3-rp-title">
                <span className="hv3-rp-ico">
                  <ClipboardGlyph />
                </span>
                {r("mockupReportTitle")}
              </p>
              <p className="hv3-rp-link">
                {r("mockupViewDetails")}
                <ChevronRightIcon />
              </p>
            </div>

            <div className="hv3-rp-cards">
              {/* biological age — the scratch-figure-green card */}
              <div className="hv3-rp-card hv3-rp-green">
                <Image
                  src={`${L}/scratch-overlay-512.png`}
                  alt=""
                  width={512}
                  height={384}
                  className="hv3-rp-tex"
                />
                <div className="hv3-rp-grain" />
                <div className="hv3-rp-body">
                  {/* Value, pill and caption are one claim, set in the data:
                      42, four years under a calendar 46. */}
                  <p className="hv3-rp-num">{s.ageValue}</p>
                  <p className="hv3-rp-lbl">{r("mockupBioAge")}</p>
                  <div className="hv3-rp-viz">
                    <div className="hv3-rp-track">
                      <i className="hv3-rp-rail" />
                      <i className="hv3-rp-origin" style={pillAt} />
                      <span className="hv3-rp-pill" style={pillAt}>
                        <ArrowDownGlyph />
                        {s.bioAge.short}
                      </span>
                    </div>
                  </div>
                  <p className="hv3-rp-foot">
                    <span className="hv3-rp-cap">{s.bioAge.desc}</span>
                  </p>
                </div>
              </div>

              {/* findings */}
              <div className="hv3-rp-card">
                <div className="hv3-rp-body">
                  <p className="hv3-rp-num">{findings}</p>
                  <p className="hv3-rp-lbl">{r("mockupFindings")}</p>
                  <div className="hv3-rp-viz">
                    <div className="hv3-rp-seg">
                      <i
                        className="hv3-rp-g1"
                        style={{ flex: s.findingsObservationCount }}
                      />
                      <i
                        className="hv3-rp-g2"
                        style={{ flex: s.findingsMinorCount }}
                      />
                    </div>
                  </div>
                  <div className="hv3-rp-foot hv3-rp-legend">
                    <p className="hv3-rp-row">
                      <i className="hv3-rp-dot hv3-rp-g2" />
                      <span className="hv3-rp-n">{s.findingsMinorCount}</span>
                      <span>{r("mockupMinor")}</span>
                    </p>
                    <p className="hv3-rp-row">
                      <i className="hv3-rp-dot hv3-rp-g1" />
                      <span className="hv3-rp-n">
                        {s.findingsObservationCount}
                      </span>
                      <span>{r("mockupObservation")}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* biomarkers — below the tile's fold on desktop; the stack shows it */}
              <div className="hv3-rp-card">
                <div className="hv3-rp-body">
                  <p className="hv3-rp-num">{biomarkers}</p>
                  <p className="hv3-rp-lbl">{r("mockupBiomarkers")}</p>
                  <div className="hv3-rp-viz">
                    <div className="hv3-rp-seg">
                      <i
                        className="hv3-rp-g1"
                        style={{ flex: s.biomarkerOptimal }}
                      />
                      <i
                        className="hv3-rp-g2"
                        style={{ flex: s.biomarkerInRange }}
                      />
                      <i
                        className="hv3-rp-g3"
                        style={{ flex: s.biomarkerOutOfRange }}
                      />
                    </div>
                  </div>
                  <div className="hv3-rp-foot hv3-rp-chips">
                    {(
                      [
                        [s.biomarkerOptimal, "mockupOptimal"],
                        [s.biomarkerInRange, "mockupInRange"],
                        [s.biomarkerOutOfRange, "mockupOutOfRange"],
                      ] as const
                    ).map(([n, k]) => (
                      <p key={k} className="hv3-rp-chip">
                        <span className="hv3-rp-n">{n}</span>
                        <span>{r(k)}</span>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Image
          src={`${L}/phone-island-alpha.png`}
          alt=""
          width={180}
          height={55}
          className="absolute"
          style={box(ISLAND.x, ISLAND.y, ISLAND.w, ISLAND.h)}
        />
      </div>
    </div>
  )
}

/**
 * Figma 314:9100 — the photograph with its progressive blur, then the action
 * row and the doctor's note popping in as two cards. A cholesterol sparkline
 * sat between them for a while; it read as noise and went (Eugene, 07.09).
 */
export function PlanHeroTile({ style }: { style?: React.CSSProperties }) {
  const t = useTranslations("_membership") as unknown as (k: string) => string
  const { hero } = useHomeV4Copy().PLAN_TILES
  return (
    <div className="hv3-t" style={style}>
      <Image
        src={`${L}/bg-hero-man-laptop-source-v3.jpg`}
        alt=""
        fill
        sizes="(min-width: 1024px) 620px, 60vw"
        className="hv3-hero-img object-cover"
      />
      {/* One action from the plan, pulled out (Figma 314:9106): icon 24 at
          19.3 in, text 19.3 after it, both centred on the 69.9 row. */}
      <div
        className="hv3-card hv3-lay hv3-lay-pop"
        style={{
          ...box(24, 20, 308, 69.9),
          ...at(0.2),
          display: "flex",
          alignItems: "center",
          gap: fp(19.3),
          paddingLeft: fp(19.3),
        }}
      >
        <span
          style={{
            width: fp(24.06),
            height: fp(24.06),
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Image
            src={`${L}/icon-nutrition.svg`}
            alt=""
            width={18}
            height={18}
            style={{ width: fp(18.05), height: fp(18.05) }}
          />
        </span>
        <div
          style={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: fp(3.84),
          }}
        >
          <p className="hv3-ct">{t(mk.overlayActionCalciumTitle)}</p>
          <p className="hv3-cd">{t(mk.overlayActionCalciumDesc)}</p>
        </div>
      </div>
      {/* The doctor's note (Figma 314:9613): 14.44/12.08 padding, avatar 43×42
          with the name centred on it, then the note 15 below. */}
      <div
        className="hv3-card hv3-lay hv3-lay-pop"
        style={{
          ...box(24, 98, 308, 140.88),
          ...at(0.55),
          display: "flex",
          flexDirection: "column",
          gap: fp(15),
          padding: `${fp(14.44)} ${fp(12.08)}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: fp(10.91) }}>
          <span
            style={{
              position: "relative",
              width: fp(43),
              height: fp(42),
              borderRadius: "999px",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Image
              src={`${L}/avatar-doctor.png`}
              alt=""
              fill
              sizes="48px"
              className="object-cover"
            />
          </span>
          <p className="hv3-ct">{hero.doctorName}</p>
        </div>
        <p className="hv3-cd">{t(mk.overlayDoctorMessage)}</p>
      </div>
    </div>
  )
}

/**
 * Figma 314:9140 — the palm photograph (heavy progressive blur), the avatar
 * with its ring and online dot, and two messages arriving one after the other.
 */
export function PlanChatTile({ style }: { style?: React.CSSProperties }) {
  // The same two expert bubbles the membership page ships, so the exchange is
  // already written and signed off in every locale. Order is greeting first,
  // recommendation second — the reverse of the membership column, which stacks
  // its chat upwards. The membership page's 0.3s / 1.1s left no gap between
  // them once each bubble also had to grow: the greeting sits alone on the
  // avatar for about three quarters of a second before the recommendation
  // arrives underneath and lifts it (Eugene, 10.09).
  const m = useTranslations("_membership") as unknown as (k: string) => string
  const bubbles: Array<[string, number]> = [
    [mk.overlayExpertBubble1, 0.3],
    [mk.overlayExpertBubble2, 1.6],
  ]
  return (
    <div className="hv3-t" style={style}>
      <Image
        src={`${L}/bg-chat.jpg`}
        alt=""
        fill
        sizes="(min-width: 1024px) 420px, 40vw"
        className="object-cover"
      />
      {/* The column is anchored to the avatar's Figma position (bottom of
          the 49px avatar at 350.74 in a 378 tile) and grows UPWARD, the way
          the membership column stacks its chat: bubbles take the height
          their text needs, so German (Eugene, 04.09) is no longer cut at
          the design's 104. Rhythm from the frame: 8 between bubbles; the
          25 to the avatar became 12 and the column sits 20 from the bottom,
          not 27, so the bubbles hang lower (Eugene, 07.09). */}
      <div
        className="hv3-chat"
        style={{ left: fp(24.7), bottom: fp(20), width: fp(326.37) }}
      >
        {bubbles.map(([key, delay]) => (
          <div key={key} className="hv3-slot" style={at(delay)}>
            <div>
              <div className="hv3-bub hv3-lay hv3-lay-pop">
                <p>{m(key)}</p>
              </div>
            </div>
          </div>
        ))}
        <div
          className="hv3-chat-me"
          style={{
            marginLeft: fp(9),
            marginTop: fp(12),
            width: fp(49),
            height: fp(49),
          }}
        >
          <span
            className="hv3-avatar hv3-lay hv3-lay-pop hv3-from-center"
            style={{ ...box(0, 0, 49, 49), ...at(0) }}
          >
            {/* The Ahead app icon, built the way the membership page's
                notification card builds it (membership-year.tsx,
                ProgressNotificationFull): the dark wave ground with the mark
                centred on it, 22 of 48. The reply is from Ahead, not from a
                person, so no stock face, and no white ring or online dot
                around a logo (Eugene, 07.09). */}
            <Image
              src="/icons/membership-bg.png"
              alt=""
              fill
              sizes="56px"
              className="object-cover"
            />
            <Image
              src="/icons/ahead-logo.svg"
              alt=""
              width={28}
              height={28}
              className="absolute inset-0 m-auto"
              style={{ width: fp(22.5), height: fp(22.5) }}
            />
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * Figma 314:9781 — one scan, cropped the way the frame crops it: the CROP
 * transform shows 89% of the image from 6% / 5% in, so the picture is drawn
 * at 168 / 0.89 and shifted by that much.
 */
export function PlanMriTile({ style }: { style?: React.CSSProperties }) {
  const size = 168 / 0.89
  return (
    <div className="hv3-t" style={style}>
      <Image
        src={`${L}/mri-sagittal.jpg`}
        alt=""
        width={1254}
        height={1254}
        sizes="240px"
        className="absolute max-w-none"
        style={box(-0.06 * size, -0.05 * size, size, size)}
      />
    </div>
  )
}

/**
 * Figma 314:10267 — the report open on a phone in someone's hands. CROP shows
 * 36% × 64% of the 16:9 photograph from 26% / 6% in; the region is square,
 * so nothing stretches.
 */
export function PlanHandTile({ style }: { style?: React.CSSProperties }) {
  const w = 168 / 0.36
  const h = 168 / 0.64
  return (
    <div className="hv3-t" style={style}>
      <Image
        src={`${L}/hands-phone.jpg`}
        alt=""
        width={1200}
        height={675}
        sizes="520px"
        className="absolute max-w-none"
        style={box(-0.26 * w, -0.06 * h, w, h)}
      />
    </div>
  )
}
